import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    addSubscription,
    fetchUserSubscriptions,
    updateSubscription,
    deleteSubscription,
    clearError,
} from "../../store/slices/subscriptionSlice";
import {
    HiOutlinePlus,
    HiOutlineTrash,
    HiOutlinePencil,
    HiOutlineDocumentText,
} from "react-icons/hi";
import { sendSubscriptionSubmissionNotification } from "../../emailjs/emailService";
import "./MySubscriptions.scss";

const MySubscriptions = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { userSubscriptions, loading, error, isAdding } = useSelector(
        (state) => state.subscriptions
    );

    const [showForm, setShowForm] = useState(false);
    const [editingSubscription, setEditingSubscription] = useState(null);
    const [formData, setFormData] = useState({
        purpose: "",
        vendor: "",
        amount: "",
        currency: "USD",
        expenseDate: "",
        invoice: "",
        notes: "",
    });
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);

    const currencies = ["USD", "EUR", "GBP"];

    useEffect(() => {
        if (user?.email) {
            dispatch(fetchUserSubscriptions(user.email));
        }
    }, [dispatch, user]);

    useEffect(() => {
        if (error) {
            alert(`Error: ${error}`);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                alert('Please select a PDF or image file (JPG, PNG)');
                return;
            }
            
            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                return;
            }
            
            setInvoiceFile(file);
            
            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => setFilePreview(e.target.result);
                reader.readAsDataURL(file);
            } else {
                setFilePreview(null);
            }
        }
    };

    const removeFile = () => {
        setInvoiceFile(null);
        setFilePreview(null);
        // Clear the file input
        const fileInput = document.getElementById('invoiceFile');
        if (fileInput) fileInput.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user?.email) {
            alert("User email not found");
            return;
        }

        const subscriptionData = {
            ...formData,
            userEmail: user.email,
            userName: user.name || user.displayName || "Unknown User",
            empID: user.empID || "",
            amount: parseFloat(formData.amount) || 0,
        };

        try {
            if (editingSubscription) {
                await dispatch(
                    updateSubscription({
                        subscriptionId: editingSubscription.id,
                        updateData: subscriptionData,
                        invoiceFile: invoiceFile,
                        existingInvoiceUrl: editingSubscription.invoiceFileUrl,
                    })
                ).unwrap();
                alert("Subscription updated successfully!");
            } else {
                await dispatch(
                    addSubscription({
                        subscriptionData,
                        invoiceFile: invoiceFile,
                    })
                ).unwrap();
                await sendSubscriptionSubmissionNotification(subscriptionData, {
                    email: user.email,
                    name: user.name || user.displayName || "Employee",
                    displayName: user.displayName || user.name || "Employee",
                });
                alert("Subscription added successfully!");
            }

            resetForm();
        } catch (error) {
            console.error("Error saving subscription:", error);
        }
    };

    const handleEdit = (subscription) => {
        setEditingSubscription(subscription);
        setFormData({
            purpose: subscription.purpose || "",
            vendor: subscription.vendor || "",
            amount: subscription.amount || "",
            currency: subscription.currency || "USD",
            expenseDate: subscription.expenseDate || "",
            invoice: subscription.invoice || "",
            notes: subscription.notes || "",
        });
        setShowForm(true);
    };

    const handleDelete = async (subscription) => {
        if (
            window.confirm("Are you sure you want to delete this subscription?")
        ) {
            try {
                await dispatch(
                    deleteSubscription({
                        subscriptionId: subscription.id,
                        invoiceFileUrl: subscription.invoiceFileUrl,
                    })
                ).unwrap();
                alert("Subscription deleted successfully!");
            } catch (error) {
                console.error("Error deleting subscription:", error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            purpose: "",
            vendor: "",
            amount: "",
            currency: "USD",
            expenseDate: "",
            invoice: "",
            notes: "",
        });
        setInvoiceFile(null);
        setFilePreview(null);
        setEditingSubscription(null);
        setShowForm(false);
        
        // Clear the file input
        const fileInput = document.getElementById('invoiceFile');
        if (fileInput) fileInput.value = '';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        let date;
        if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            date = new Date(timestamp);
        }
        return date.toLocaleDateString();
    };

    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
        }).format(amount || 0);
    };

    return (
        <div className="my-subscriptions-container">
            <div className="page-header">
                <h1>My Subscriptions</h1>
                <p>Track your business subscription expenses</p>
                <button className="add-btn" onClick={() => setShowForm(true)}>
                    <HiOutlinePlus /> Add Subscription
                </button>
            </div>

            {showForm && (
                <div className="form-modal">
                    <div className="form-content">
                        <div className="form-header">
                            <h2>
                                {editingSubscription
                                    ? "Edit Subscription"
                                    : "Add New Subscription"}
                            </h2>
                            <button className="close-btn" onClick={resetForm}>
                                ×
                            </button>
                        </div>

                        <form
                            onSubmit={handleSubmit}
                            className="subscription-form"
                        >
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="purpose">Purpose *</label>
                                    <input
                                        type="text"
                                        id="purpose"
                                        name="purpose"
                                        value={formData.purpose}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Web Hosting, Email Marketing"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="vendor">Vendor *</label>
                                    <input
                                        type="text"
                                        id="vendor"
                                        name="vendor"
                                        value={formData.vendor}
                                        onChange={handleInputChange}
                                        placeholder="e.g., AWS, Mailchimp, GoDaddy"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="amount">Amount *</label>
                                    <input
                                        type="number"
                                        id="amount"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleInputChange}
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="currency">Currency *</label>
                                    <select
                                        id="currency"
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {currencies.map((curr) => (
                                            <option key={curr} value={curr}>
                                                {curr}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="expenseDate">
                                        Expense Date *
                                    </label>
                                    <input
                                        type="date"
                                        id="expenseDate"
                                        name="expenseDate"
                                        value={formData.expenseDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="invoice">
                                        Invoice/Reference
                                    </label>
                                    <input
                                        type="text"
                                        id="invoice"
                                        name="invoice"
                                        value={formData.invoice}
                                        onChange={handleInputChange}
                                        placeholder="Invoice number or reference"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">Notes</label>
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Additional notes or details"
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="invoiceFile">Invoice Attachment</label>
                                <input
                                    type="file"
                                    id="invoiceFile"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={handleFileChange}
                                    className="file-input"
                                />
                                <p className="file-info">
                                    Supported formats: PDF, JPG, PNG (Max 5MB)
                                </p>
                                
                                {(invoiceFile || filePreview) && (
                                    <div className="file-preview">
                                        <div className="file-info-box">
                                            <span className="file-name">
                                                {invoiceFile ? invoiceFile.name : 'Current file'}
                                            </span>
                                            <button
                                                type="button"
                                                className="remove-file-btn"
                                                onClick={removeFile}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        
                                        {filePreview && (
                                            <div className="image-preview">
                                                <img src={filePreview} alt="Invoice preview" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {editingSubscription?.invoiceFileUrl && !invoiceFile && (
                                    <div className="existing-file">
                                        <span>Current file: {editingSubscription.invoiceFileName || 'invoice file'}</span>
                                        <button
                                            type="button"
                                            className="view-file-btn"
                                            onClick={() => window.open(editingSubscription.invoiceFileUrl, '_blank')}
                                        >
                                            View
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={isAdding}
                                >
                                    {isAdding
                                        ? "Saving..."
                                        : editingSubscription
                                        ? "Update"
                                        : "Add Subscription"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="subscriptions-content">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading subscriptions...</p>
                    </div>
                ) : userSubscriptions.length === 0 ? (
                    <div className="empty-state">
                        <HiOutlineDocumentText className="empty-icon" />
                        <h3>No Subscriptions Found</h3>
                        <p>You haven't added any subscription records yet.</p>
                        <button
                            className="add-btn"
                            onClick={() => setShowForm(true)}
                        >
                            <HiOutlinePlus /> Add Your First Subscription
                        </button>
                    </div>
                ) : (
                    <div className="subscriptions-grid">
                        {userSubscriptions.map((subscription) => (
                            <div
                                key={subscription.id}
                                className="subscription-card"
                            >
                                <div className="card-header">
                                    <h3>{subscription.purpose}</h3>
                                    <div className="card-actions">
                                        <button
                                            className="edit-btn"
                                            onClick={() =>
                                                handleEdit(subscription)
                                            }
                                            title="Edit"
                                        >
                                            <HiOutlinePencil />
                                        </button>
                                        <button
                                            className="delete-btn"
                                            onClick={() =>
                                                handleDelete(subscription)
                                            }
                                            title="Delete"
                                        >
                                            <HiOutlineTrash />
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="info-row">
                                        <span className="label">Vendor:</span>
                                        <span className="value">
                                            {subscription.vendor}
                                        </span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Amount:</span>
                                        <span className="value amount">
                                            {formatCurrency(
                                                subscription.amount,
                                                subscription.currency
                                            )}
                                        </span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Date:</span>
                                        <span className="value">
                                            {formatDate(
                                                subscription.expenseDate
                                            )}
                                        </span>
                                    </div>

                                    {subscription.invoice && (
                                        <div className="info-row">
                                            <span className="label">
                                                Invoice:
                                            </span>
                                            <span className="value">
                                                {subscription.invoice}
                                            </span>
                                        </div>
                                    )}

                                    {subscription.notes && (
                                        <div className="info-row">
                                            <span className="label">
                                                Notes:
                                            </span>
                                            <span className="value">
                                                {subscription.notes}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {subscription.invoiceFileUrl && (
                                        <div className="info-row">
                                            <span className="label">
                                                Invoice:
                                            </span>
                                            <span className="value">
                                                <button
                                                    className="view-invoice-btn"
                                                    onClick={() => window.open(subscription.invoiceFileUrl, '_blank')}
                                                    title="View invoice file"
                                                >
                                                    {subscription.invoiceFileName || 'View File'}
                                                </button>
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="card-footer">
                                    <small>
                                        Added:{" "}
                                        {formatDate(subscription.createdAt)}
                                    </small>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MySubscriptions;
